import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { Alert, Box, CircularProgress, Link, Stack, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { deviceType } from 'react-device-detect';
import ReactGA from 'react-ga4';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import VerificationInput from 'react-verification-input';

import SubmitButton from 'components/common/formikForm/SubmitButton';
import AuthLayout, { authSubmitButtonSx } from 'views/v2/auth/components/authLayout';

import api from 'api';
import { BackendEventProps } from 'api/models/payload/EventsPayload';
import ROLE from 'assets/userRoles.json';
import { useInflight } from 'hooks/useInflight';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { AppDispatch, RootState } from 'redux/store';
import { getLocation } from 'utils/location';

interface VerificationData {
    email: string;
    name: string;
    code: string;
}

type ResendView = 'idle' | 'sending' | 'success' | 'error';
type CheckIssue = 'expired' | 'rateLimited' | null;

interface VerifyResult {
    ok: boolean;
    rateLimited?: boolean;
}

const initialState: VerificationData = {
    email: '',
    name: '',
    code: '',
};

const verificationInputSx = {
    width: '100%',
    '& .verification-input__container': {
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: { xs: '8px', sm: '10px' },
        width: '100%',
    },
    '& .verification-input__character': {
        height: { xs: 48, sm: 52 },
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
        color: 'text.primary',
        fontSize: { xs: 18, sm: 20 },
        fontWeight: 600,
        textAlign: 'center',
        lineHeight: { xs: '48px', sm: '52px' },
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
    },
    '& .verification-input__character--inactive': {
        color: 'text.disabled',
    },
    '& .verification-input__character--selected': {
        borderColor: 'primary.main',
        bgcolor: 'background.paper',
        boxShadow: '0 0 0 3px rgba(96, 37, 216, 0.12)',
        outline: 'none',
    },
    '&.verification-input--disabled': {
        opacity: 0.6,
        pointerEvents: 'none',
        '& .verification-input__character': {
            bgcolor: 'action.disabledBackground',
            color: 'text.disabled',
        },
    },
};

const statusIconBoxSx = (bgcolor: string, color: string) => ({
    width: 64,
    height: 64,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor,
    color,
});

/**
 * Public page reached from the one-time verification link in the invite email:
 *   /user-verification?email=<email>&code=<code>[&name=<name>]
 * Auto-verifies when both email and code are present; otherwise shows the
 * manual entry form. On success, stores the pair in localStorage and
 * navigates to /set-password.
 */
export default function VerificationCodeV2() {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const [queryParameters] = useSearchParams();
    const { role } = useSelector((state: RootState) => state.user.entity);
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);
    const country: string = getLocation().userCountry;

    const [data, setData] = useState<VerificationData>(initialState);
    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [isCodeCheckComplete, setIsCodeCheckComplete] = useState(false);
    const [isCodeValid, setIsCodeValid] = useState(false);
    const [checkIssue, setCheckIssue] = useState<CheckIssue>(null);
    const [resendView, setResendView] = useState<ResendView>('idle');
    const [resendError, setResendError] = useState('');

    // De-dupe the URL-param mount check. Tracks the last (email|code) pair we
    // already verified so StrictMode double-invoke or a re-render that changes
    // queryParameters identity can't fan out into a second HTTP call.
    const verifiedKeyRef = useRef<string | null>(null);

    const completeVerification = (email: string, code: string) => {
        localStorage.setItem('email', JSON.stringify(email));
        localStorage.setItem('verification', JSON.stringify(code));
        navigate('/set-password');
    };

    const handleUpdate = (value: string) => {
        setData((prev) => ({ ...prev, code: value }));
        setButtonDisabled(value.length !== 6);
        // User edited the code after a failed auto-check — treat as a
        // fresh attempt so Verify Account can show loading again.
        if (checkIssue) setCheckIssue(null);
    };

    const validateClientSide = () => {
        if (!data.email || !data.email.includes('@')) {
            dispatch(
                setAlert({
                    message: 'Please enter a valid email address',
                    type: 'error',
                })
            );
            return false;
        }
        if (!data.code || data.code.length < 6) {
            dispatch(setAlert({ message: 'Invalid Code', type: 'error' }));
            return false;
        }
        return true;
    };

    const handleResend = async (signal: AbortSignal) => {
        if (!data.email) {
            setResendError('Unable to resend. Missing email address.');
            setResendView('error');
            return;
        }

        setResendError('');
        setResendView('sending');

        try {
            const res = await api.authentication.sendAuthenticationCode(data.email);
            if (signal.aborted) return;
            if (res.status === 200 || res.status === 201) {
                // Clear the expired/stale code so the user enters the new one.
                setData((prev) => ({ ...prev, code: '' }));
                setButtonDisabled(true);
                setCheckIssue(null);
                setResendView('success');
                return;
            }
            setResendError('Something went wrong. Please try again.');
            setResendView('error');
        } catch (err: unknown) {
            if (signal.aborted) return;
            const status = (err as { response?: { status?: number; data?: { msg?: string } } })
                ?.response?.status;
            const msg =
                (err as { response?: { data?: { msg?: string } } })?.response?.data?.msg ||
                'Something went wrong. Please try again.';
            if (status === 404) {
                setResendError(msg || 'Something went wrong.');
            } else {
                setResendError(msg);
            }
            setResendView('error');
        }
    };
    const { pending: resending, run: runResend } = useInflight(handleResend);

    // Shared verify logic. URL auto-check does NOT use the form's inflight
    // pending flag — otherwise an expired link leaves "Verify Account" stuck
    // in a loading state after the check finishes / fails.
    const submitVerification = async (
        signal: AbortSignal | null,
        email: string,
        code: string,
        { silent }: { silent?: boolean } = {}
    ): Promise<VerifyResult> => {
        const key = `${email}|${code}`;
        if (verifiedKeyRef.current === key) return { ok: true };
        try {
            const res = await api.authentication.checkAuthenticationCode(email, code);
            if (signal?.aborted) return { ok: false };
            if (res.status === 200 || res.status === 201) {
                verifiedKeyRef.current = key;
                completeVerification(email, code);
                return { ok: true };
            }
            if (!silent) {
                const msg = (res.data as { msg?: string })?.msg || 'Invalid Code';
                dispatch(setAlert({ message: msg, type: 'error' }));
            }
            return { ok: false };
        } catch (err: unknown) {
            if (signal?.aborted) return { ok: false };
            const status = (err as AxiosError<{ msg?: string }>)?.response?.status;
            if (status === 429) {
                setCheckIssue('rateLimited');
                if (!silent) {
                    dispatch(
                        setAlert({
                            message: translate('exceededMaximalAttempts'),
                            type: 'error',
                        })
                    );
                }
                return { ok: false, rateLimited: true };
            }
            if (silent) return { ok: false };
            const response = (err as AxiosError<{ msg?: string }>)?.response;
            if (response?.status === 404) {
                dispatch(setAlert({ message: 'Invalid email', type: 'error' }));
            } else {
                const message = response?.data?.msg || 'Invalid Code';
                dispatch(setAlert({ message, type: 'error' }));
            }
            return { ok: false };
        }
    };

    // Manual form submit only — drives the Verify Account button spinner.
    const { pending: verifying, run: runVerify } = useInflight(
        async (signal, email: string, code: string) => submitVerification(signal, email, code)
    );

    useEffect(() => {
        const email = queryParameters.get('email');
        const name = queryParameters.get('name');
        const code = queryParameters.get('code');

        setData((prev) => ({
            email: email || prev.email,
            name: name || prev.name,
            code: code || prev.code,
        }));

        if (code) {
            setButtonDisabled(code.length !== 6);
        }

        const hasAuthParams = Boolean(email && code);
        if (!hasAuthParams) {
            setIsCodeCheckComplete(true);
            setIsCodeValid(false);
            return;
        }

        const key = `${email}|${code}`;
        if (verifiedKeyRef.current === key) {
            setIsCodeCheckComplete(true);
            setIsCodeValid(true);
            return;
        }

        const ctrl = new AbortController();
        (async () => {
            // Silent auto-check: surface the failure via subtitle, not setAlert
            // spam, and never toggle the form button's loading state.
            const result = await submitVerification(ctrl.signal, email as string, code as string, {
                silent: true,
            });
            if (ctrl.signal.aborted) return;
            setIsCodeCheckComplete(true);
            setIsCodeValid(result.ok);
            if (!result.ok && !result.rateLimited) setCheckIssue('expired');
        })();
        return () => {
            ctrl.abort();
        };
        // Key only on the (email|code) pair so a new queryParameters reference
        // doesn't trigger a second GET.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryParameters.get('email'), queryParameters.get('code')]);

    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: `${window.location.pathname}`,
            title: 'VerificationCodePage',
        });
    }, []);

    const handleStoreCampaignEventProps = async () => {
        const url_params = window.document.referrer
            ? new URL(window.document.referrer).searchParams
            : null;
        const event_properties: BackendEventProps = {
            country: country,
            referrer: window.document.referrer,
            device_type: deviceType,
            utm_source: queryParameters.get('utm_source') || url_params?.get('utm_source') || '',
            utm_medium: queryParameters.get('utm_medium') || url_params?.get('utm_medium') || '',
            utm_campaign:
                queryParameters.get('utm_campaign') || url_params?.get('utm_campaign') || '',
            utm_content: queryParameters.get('utm_content') || url_params?.get('utm_content') || '',
            user_agent: window.navigator.userAgent,
            anonymous_id: localStorage.ajs_anonymous_id?.replaceAll('"', ''),
        };
        const res = await api.events.user_props_put(event_properties);
        return res.data;
    };

    useEffect(() => {
        if (role && isAuthenticated) {
            handleStoreCampaignEventProps();
            role === ROLE.externalClient
                ? navigate('/visa-process/1/visa-offers')
                : navigate('/dashboard');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [role, isAuthenticated]);

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!validateClientSide()) return;
        runVerify(data.email, data.code);
    };

    const backToLogin = (
        <Link
            component={RouterLink}
            to="/login"
            underline="hover"
            variant="body2"
            onClick={(e) => {
                e.preventDefault();
                navigate('/login');
            }}
            sx={{
                fontWeight: 600,
                color: 'primary.main',
                py: 0.5,
            }}
        >
            Back to login
        </Link>
    );

    if (!isCodeCheckComplete) {
        return (
            <AuthLayout title="Verifying…" subtitle="Please wait while we check your link">
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={32} />
                </Box>
            </AuthLayout>
        );
    }

    // Valid code auto-navigates to /set-password; keep shell during transition.
    if (isCodeValid) {
        return (
            <AuthLayout title="Verified" subtitle="Redirecting to set your password…">
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={32} />
                </Box>
            </AuthLayout>
        );
    }

    // ── Resend: sending ──────────────────────────────────────────────────
    if (resendView === 'sending' || resending) {
        return (
            <AuthLayout
                title="Sending verification link"
                subtitle="Please wait while we send a new link to your email"
                footer={backToLogin}
            >
                <Stack spacing={2.5} sx={{ alignItems: 'center', textAlign: 'center' }}>
                    {data.email ? (
                        <Box
                            sx={{
                                width: '100%',
                                fontSize: 14,
                                fontWeight: 500,
                                color: 'text.secondary',
                                bgcolor: 'action.hover',
                                borderRadius: 1.5,
                                px: 2,
                                py: 1.25,
                                wordBreak: 'break-all',
                                textAlign: 'center',
                            }}
                        >
                            {data.email}
                        </Box>
                    ) : null}
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={36} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        Sending…
                    </Typography>
                </Stack>
            </AuthLayout>
        );
    }

    // ── Resend: success ──────────────────────────────────────────────────
    if (resendView === 'success') {
        return (
            <AuthLayout
                title="Verification link sent"
                subtitle="Check your inbox for a new verification link"
                footer={backToLogin}
            >
                <Stack spacing={2.5} sx={{ alignItems: 'center', textAlign: 'center' }}>
                    <Box sx={statusIconBoxSx('rgba(16, 185, 129, 0.12)', 'success.main')}>
                        <CheckCircleOutlineRoundedIcon sx={{ fontSize: 36 }} />
                    </Box>

                    {data.email ? (
                        <Box
                            sx={{
                                width: '100%',
                                fontSize: 14,
                                fontWeight: 500,
                                color: 'text.secondary',
                                bgcolor: 'action.hover',
                                borderRadius: 1.5,
                                px: 2,
                                py: 1.25,
                                wordBreak: 'break-all',
                                textAlign: 'center',
                            }}
                        >
                            {data.email}
                        </Box>
                    ) : null}

                    <Stack spacing={1}>
                        <Typography
                            variant="body1"
                            color="text.primary"
                            sx={{ fontWeight: 500, lineHeight: 1.5 }}
                        >
                            We sent a new verification link to your email.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            If you don&apos;t see it, check your spam folder.
                        </Typography>
                    </Stack>

                    <SubmitButton
                        type="button"
                        fullWidth
                        size="large"
                        sx={authSubmitButtonSx}
                        onClick={() => setResendView('idle')}
                    >
                        Enter verification code
                    </SubmitButton>
                </Stack>
            </AuthLayout>
        );
    }

    // ── Resend: error ────────────────────────────────────────────────────
    if (resendView === 'error') {
        return (
            <AuthLayout
                title="Could not send link"
                subtitle="We were unable to resend the verification link"
                footer={backToLogin}
            >
                <Stack spacing={2.5} sx={{ alignItems: 'center', textAlign: 'center' }}>
                    <Box sx={statusIconBoxSx('rgba(239, 68, 68, 0.12)', 'error.main')}>
                        <ErrorOutlineRoundedIcon sx={{ fontSize: 36 }} />
                    </Box>

                    <Alert
                        severity="error"
                        variant="outlined"
                        sx={{
                            width: '100%',
                            borderRadius: 2,
                            textAlign: 'left',
                        }}
                    >
                        {resendError || 'Something went wrong. Please try again.'}
                    </Alert>

                    <SubmitButton
                        type="button"
                        fullWidth
                        size="large"
                        sx={authSubmitButtonSx}
                        onClick={() => {
                            setResendError('');
                            runResend();
                        }}
                    >
                        Try again
                    </SubmitButton>
                </Stack>
            </AuthLayout>
        );
    }

    // ── Default: enter code form ─────────────────────────────────────────
    const title = data.name ? `Welcome ${data.name}` : 'Verify your account';
    const subtitle =
        checkIssue === 'rateLimited'
            ? translate('exceededMaximalAttempts')
            : checkIssue === 'expired'
              ? translate('linkExpired')
              : translate('enterVerificationCode');

    return (
        <AuthLayout title={title} subtitle={subtitle} footer={backToLogin}>
            <Box component="form" noValidate onSubmit={handleFormSubmit} aria-busy={verifying}>
                <Stack spacing={2}>
                    {data.email ? (
                        <Box
                            sx={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: 'text.secondary',
                                bgcolor: 'action.hover',
                                borderRadius: 1.5,
                                px: 2,
                                py: 1.25,
                                wordBreak: 'break-all',
                                textAlign: 'center',
                            }}
                        >
                            {data.email}
                        </Box>
                    ) : null}

                    <Box
                        sx={verificationInputSx}
                        className={verifying ? 'verification-input--disabled' : undefined}
                        aria-disabled={verifying}
                    >
                        <VerificationInput
                            validChars="0-9"
                            inputProps={{
                                inputMode: 'numeric',
                                autoComplete: 'one-time-code',
                                disabled: verifying,
                                readOnly: verifying,
                            }}
                            containerProps={{
                                // Keep focus styles off while the request is in flight.
                                style: verifying ? { pointerEvents: 'none' } : undefined,
                            }}
                            classNames={{
                                container: 'verification-input__container',
                                character: 'verification-input__character',
                                characterInactive: 'verification-input__character--inactive',
                                characterSelected: 'verification-input__character--selected',
                            }}
                            onChange={verifying ? undefined : handleUpdate}
                            value={data.code}
                            placeholder=""
                        />
                    </Box>

                    <Stack direction="row" sx={{ justifyContent: 'flex-end', mt: -0.5 }}>
                        <Link
                            component="button"
                            type="button"
                            underline="hover"
                            variant="body2"
                            disabled={!data.email || verifying}
                            onClick={(e) => {
                                e.preventDefault();
                                if (!data.email || resending || verifying) return;
                                runResend();
                            }}
                            sx={{
                                fontWeight: 500,
                                color: 'text.secondary',
                                py: 0.5,
                                border: 'none',
                                background: 'none',
                                cursor: data.email && !verifying ? 'pointer' : 'default',
                                opacity: data.email && !verifying ? 1 : 0.6,
                            }}
                        >
                            Resend Verification Link
                        </Link>
                    </Stack>

                    <SubmitButton
                        loading={verifying}
                        disabled={buttonDisabled || verifying}
                        fullWidth
                        size="large"
                        sx={authSubmitButtonSx}
                    >
                        Verify Account
                    </SubmitButton>
                </Stack>
            </Box>
        </AuthLayout>
    );
}
